import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FileText, RotateCcw, X } from 'lucide-react';
import { useStore, useStoreQuery } from '../db/context';
import { ConfirmDialog } from './Dialog';
import { ModuleIcon } from './ModuleIcon';

export function TrashView() {
  const { t, i18n } = useTranslation();
  const store = useStore();
  const [confirm, setConfirm] = useState<{ id: string; title: string } | null>(null);
  const trashed = useStoreQuery((s) => s.listPages({ trashed: true }), [], ['pages']);
  const ids = new Set(trashed?.map((p) => p.id));
  // Show only the top of each trashed subtree; children go along with it.
  const roots = trashed?.filter((p) => !p.parentId || !ids.has(p.parentId)) ?? [];
  const fmt = new Intl.DateTimeFormat(i18n.language, { dateStyle: 'medium', timeStyle: 'short' });

  return (
    <section className="mx-auto w-full max-w-6xl px-6 py-6 sm:px-10">
      <h1 className="flex items-center gap-3 text-3xl font-bold tracking-tight">
        <ModuleIcon kind="trash" size="lg" />
        {t('trash.title')}
      </h1>
      {roots.length === 0 ? (
        <p className="mt-6 text-fg-muted">{t('trash.empty')}</p>
      ) : (
        <ul className="mt-6 divide-y divide-border rounded-xl border border-border">
          {roots.map((p) => {
            const title = p.title || t('page.untitled');
            return (
              <li key={p.id} className="flex items-center gap-3 px-3 py-2">
                <FileText className="size-4 text-fg-muted" />
                <span className="min-w-0 flex-1 truncate">{title}</span>
                <span className="text-xs text-fg-muted">
                  {p.trashedAt && fmt.format(p.trashedAt)}
                </span>
                <button
                  type="button"
                  onClick={() => void store.restorePage(p.id)}
                  className="flex items-center gap-1 rounded-md px-2 py-1 text-sm hover:bg-bg-hover"
                >
                  <RotateCcw className="size-3.5" />
                  {t('trash.restore')}
                </button>
                <button
                  type="button"
                  onClick={() => setConfirm({ id: p.id, title })}
                  className="flex items-center gap-1 rounded-md px-2 py-1 text-sm text-danger hover:bg-bg-hover"
                >
                  <X className="size-3.5" />
                  {t('trash.deleteForever')}
                </button>
              </li>
            );
          })}
        </ul>
      )}
      <ConfirmDialog
        open={confirm !== null}
        onOpenChange={(o) => !o && setConfirm(null)}
        title={t('trash.deleteForever')}
        description={t('trash.confirmDelete', { title: confirm?.title ?? '' })}
        confirmLabel={t('trash.deleteForever')}
        cancelLabel={t('app.cancel')}
        onConfirm={() => confirm && void store.purgePage(confirm.id)}
      />
    </section>
  );
}
